import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, ChevronRight, Wifi, Car, Utensils, Dumbbell, Wind, Tv, Coffee, Bath, Waves, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import BookingModal from '../components/BookingModal';
import ImageSlider from '../components/ImageSlider';
import LocationSection from '../components/LocationSection';
import AvailabilityChecker from '../components/AvailabilityChecker';
import RoomDetailsModal from '../components/RoomDetailsModal';
import PackageDetailsModal from '../components/PackageDetailsModal';
import { useHotel } from '../context/HotelContext';
import apiClient from '../lib/apiClient';
import NoDataError from '../components/NoDataError';

const FACILITY_ICON_MAP = {
  WIFI: Wifi, Wifi: Wifi,
  AC: Wind, Air: Wind,
  Parking: Car, Car: Car,
  Restaurant: Utensils, Dining: Utensils, Food: Utensils,
  Gym: Dumbbell, Fitness: Dumbbell,
  TV: Tv,
  Coffee: Coffee, Breakfast: Coffee,
  Bath: Bath, Pool: Waves, Jacuzzi: Waves,
  Balcony: Flame,
};

function getFacilityIcon(iconStr) {
  if (!iconStr) return Wifi;
  const key = Object.keys(FACILITY_ICON_MAP).find(k =>
    iconStr.toLowerCase().includes(k.toLowerCase())
  );
  return key ? FACILITY_ICON_MAP[key] : Wifi;
}

const Home = () => {
  const navigate = useNavigate();
  const { hotel } = useHotel();

  const [homeData, setHomeData] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedBookingItem, setSelectedBookingItem] = useState(null);
  const [availabilityCheckerOpen, setAvailabilityCheckerOpen] = useState(false);
  const [roomDetailsOpen, setRoomDetailsOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [packageDetailsOpen, setPackageDetailsOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.post('/api/Home/GetHome'),
      apiClient.get('/api/Review/GetReviews').catch(() => []),
    ])
      .then(([home, reviews]) => {
        if (!home) { setError(true); return; }
        setHomeData(home);
        setFacilities(home?.about?.features || []);
        setTestimonials(reviews || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTestimonial(prev =>
        prev === testimonials.length - 1 ? 0 : prev + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      * { scroll-behavior: smooth; }
      .animate-fade-in-up, .animate-slide-in-left, .animate-zoom-in {
        will-change: transform, opacity;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const handleBookRoom = (room) => {
    setSelectedBookingItem({ ...room, type: 'room' });
    setAvailabilityCheckerOpen(true);
  };

  const handleQuickBook = () => navigate('/rooms');

  const handleProceedToBook = (availabilityData) => {
    setAvailabilityCheckerOpen(false);
    if (selectedBookingItem) {
      setSelectedBookingItem({
        ...selectedBookingItem,
        prefilledData: {
          checkIn: availabilityData.checkIn,
          checkOut: availabilityData.checkOut,
          guests: availabilityData.guests
        }
      });
    }
    setBookingModalOpen(true);
  };

  const handleViewRoomDetails = (room) => {
    setSelectedRoom({
      ...room,
      amenities: (room.amenities || []).map(a => typeof a === 'string' ? { name: a } : a),
    });
    setRoomDetailsOpen(true);
  };

  const hero = homeData?.hero;
  const heroSliders = homeData?.heroSliders || [];
  const about = homeData?.about;
  const featuredRooms = homeData?.featuredRooms || [];
  const hotelName = hotel?.hotelName || about?.title || '';

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-50">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6"></div>
        <p className="text-gray-500 text-sm tracking-widest uppercase">Loading</p>
      </div>
    );
  }

  if (error) {
    return <NoDataError message="We couldn't load the hotel information. Please try again later." />;
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {heroSliders.length > 0 ? (
            <ImageSlider images={heroSliders} autoPlay={true} interval={5000} />
          ) : hotel?.bannerUrl ? (
            <img src={hotel.bannerUrl} alt={hotel?.hotelName || 'Hotel'} className="w-full h-full object-cover brightness-75" />
          ) : null}
        </div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-transparent to-blue-600/20 animate-gradient"></div>

        <div className="absolute top-20 left-10 w-16 h-16 bg-white/10 rounded-full animate-float"></div>
        <div className="absolute top-40 right-20 w-8 h-8 bg-blue-400/30 rounded-full animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-32 left-32 w-12 h-12 bg-white/20 rounded-full animate-float" style={{animationDelay: '2s'}}></div>

        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white z-10 max-w-4xl px-6">
            <h1 className="mb-6 animate-fade-in-up opacity-0 text-shadow-lg" style={{animationDelay: '0.2s'}}>
              {hero?.title || (hotelName ? `Welcome to ${hotelName}` : '')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 animate-slide-in-left opacity-0 text-shadow-md" style={{animationDelay: '0.6s'}}>
              {hero?.subtitle || hotel?.description || ''}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-zoom-in opacity-0" style={{animationDelay: '1s'}}>
              <button
                onClick={handleQuickBook}
                className="btn btn-primary text-lg px-8 py-4 transform hover:scale-105 transition-all duration-300 animate-pulse"
              >
                Book Now
              </button>
              <Link to="/about" className="btn btn-outline text-lg px-8 py-4 transform hover:scale-105 transition-all duration-300">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section-large bg-gray-50 overflow-hidden">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left opacity-0" style={{animationDelay: '0.2s'}}>
              <h2 className="mb-6">{about?.title ? `About ${about.title}` : hotelName ? `About ${hotelName}` : ''}</h2>
              <p className="text-lg mb-8 leading-relaxed">
                {about?.description || hotel?.description || ''}
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-all duration-300 transform hover:translate-x-2"
              >
                Read More <ChevronRight className="ml-2" size={20} />
              </Link>
            </div>
            {facilities.length > 0 && (
              <div className="grid grid-cols-2 gap-6">
                {facilities.slice(0, 4).map((facility, index) => {
                  const IconComponent = getFacilityIcon(facility.title || facility.name);
                  return (
                    <div
                      key={index}
                      className="bg-white p-6 rounded-xl shadow-sm text-center transform hover:scale-105 transition-all duration-300 animate-fade-in-up opacity-0"
                      style={{animationDelay: `${0.4 + index * 0.1}s`}}
                    >
                      <IconComponent className="mx-auto mb-4 text-blue-600 animate-float" size={32} />
                      <h4 className="font-semibold mb-2">{facility.title || facility.name}</h4>
                      <p className="text-sm text-gray-600">{facility.description?.trim()}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Rooms Section */}
      <section className="section-large overflow-hidden">
        <div className="container">
          <div className="text-center mb-12 animate-fade-in-up opacity-0" style={{animationDelay: '0.1s'}}>
            <h2 className="mb-4">Our Featured Rooms</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover our carefully curated selection of rooms, each designed to provide
              the perfect blend of comfort, style, and luxury.
            </p>
          </div>

          {featuredRooms.length === 0 ? (
            <div className="text-center text-gray-400 py-12">No rooms available.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {featuredRooms.map((room, index) => (
                <div
                  key={room.id}
                  className="card group transform hover:scale-105 transition-all duration-500 animate-zoom-in opacity-0"
                  style={{animationDelay: `${0.2 + index * 0.1}s`}}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={room.images?.[0] || room.image}
                      alt={room.name}
                      className="img-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full font-semibold animate-pulse">
                      ৳{room.price.toLocaleString()}/night
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-blue-600 transition-colors duration-300">{room.name}</h3>
                    {room.features?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {room.features.map((feature, idx) => (
                          <span key={idx} className="text-sm bg-gray-100 px-3 py-1 rounded-full text-gray-700 hover:bg-blue-100 hover:text-blue-700 transition-colors duration-300">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Button
                        onClick={() => handleBookRoom(room)}
                        className="w-full transform hover:scale-105 transition-transform duration-300"
                      >
                        Book This Room
                      </Button>
                      <Button
                        onClick={() => handleViewRoomDetails(room)}
                        variant="outline"
                        className="w-full transform hover:scale-105 transition-transform duration-300"
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center animate-fade-in-up opacity-0" style={{animationDelay: '1s'}}>
            <Link to="/rooms" className="btn btn-secondary transform hover:scale-105 transition-all duration-300">
              View All Rooms
            </Link>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <LocationSection />

      {/* Testimonials Section */}
      <section className="section-large">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="mb-4">What Our Guests Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Read what our valued guests have to say about their experience at {hotelName}.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {testimonials.length === 0 ? (
              <div className="text-center text-gray-400 py-12">No reviews yet.</div>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
                  <div className="flex justify-center mb-6">
                    {[...Array(parseInt(testimonials[currentTestimonial].rating) || 0)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-current" size={24} />
                    ))}
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-800 mb-8 leading-relaxed">
                    "{testimonials[currentTestimonial].comment}"
                  </blockquote>
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xl">
                      {testimonials[currentTestimonial].name?.charAt(0) || '?'}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-lg">{testimonials[currentTestimonial].name}</div>
                      {testimonials[currentTestimonial].location && (
                        <div className="text-gray-600">{testimonials[currentTestimonial].location}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center space-x-2 mt-8">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentTestimonial ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      onClick={() => setCurrentTestimonial(index)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-large bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container text-center">
          <h2 className="mb-6 text-white">Ready to Experience {hotelName}?</h2>
          <p className="text-xl mb-8 text-blue-100 max-w-2xl mx-auto">
            Book your stay today and discover why guests choose {hotelName} for their
            business and leisure travel needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleQuickBook} className="btn btn-outline text-lg px-8 py-4">
              Book Now
            </button>
            <Link to="/rooms" className="btn bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4">
              View Roomss
            </Link>
          </div>
        </div>
      </section>

      {availabilityCheckerOpen && (
        <AvailabilityChecker
          room={selectedBookingItem}
          onProceedToBook={handleProceedToBook}
          onClose={() => setAvailabilityCheckerOpen(false)}
        />
      )}

      <RoomDetailsModal
        isOpen={roomDetailsOpen}
        onClose={() => setRoomDetailsOpen(false)}
        room={selectedRoom}
        onBookRoom={handleBookRoom}
      />

      <PackageDetailsModal
        isOpen={packageDetailsOpen}
        onClose={() => setPackageDetailsOpen(false)}
        package={selectedPackage}
        onBookPackage={(pkg) => {
          setSelectedBookingItem({ ...pkg, type: 'package' });
          setBookingModalOpen(true);
        }}
      />

      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        roomData={selectedBookingItem?.type === 'room' ? selectedBookingItem : null}
        packageData={selectedBookingItem?.type === 'package' ? selectedBookingItem : null}
      />
    </div>
  );
};

export default Home;
